"use client";

import React, { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const teamMembers = [{
  name: "Dr. Lawrence Miller",
  position: "Lead Sleep Dentist",
  bio: "A Diplomate of the American Board of Dental Sleep Medicine with over 15 years of experience providing CPAP alternatives in Los Angeles.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621270/img/team_dentist_lead.png"
}, {
  name: "Dr. Sarah Chen",
  position: "Clinical Director",
  bio: "Specializing in mandibular advancement devices, Dr. Chen focuses on personalized care for patients throughout Beverly Hills and Santa Monica.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621270/img/team_hygienist.png"
}];
export default function OurExpertTeam() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="137170" id="our-team" className="relative w-full overflow-hidden bg-background py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-[10%] -top-[20%] h-[60%] w-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -right-[10%] top-[40%] h-[60%] w-[40%] rounded-full bg-secondary/10 blur-[100px]" />
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-2xl text-center transition-all duration-1000 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-sans text-3xl font-bold tracking-tight text-secondary sm:text-4xl lg:text-5xl">
            Our Expert Sleep Team
          </h2>
          <p className="mt-4 text-lg leading-8 text-muted-foreground">
            Meet the dedicated Los Angeles professionals committed to providing you with
            exceptional, comfortable, and modern dental sleep medicine solutions.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 md:max-w-none md:grid-cols-3 lg:gap-12">
          {teamMembers.map((member, i) => <TeamMemberCard key={i} member={member} index={i} />)}
        </div>
      </div>
    </section>;
}
function TeamMemberCard({
  member,
  index
}: {
  member: typeof teamMembers[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div data-index={index} ref={ref} className={`group relative aspect-[3/4] w-full overflow-hidden rounded-2xl bg-card shadow-md transition-all duration-1000 ease-out hover:shadow-xl ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      {}
      <img src={member.image} alt={member.name} className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 ease-in-out group-hover:scale-105" />

      {}
      <div className="absolute inset-x-0 bottom-0 translate-y-[calc(100%-6rem)] border-t border-primary/10 bg-card/90 p-6 backdrop-blur-xl transition-transform duration-500 ease-in-out group-hover:translate-y-0 dark:bg-card/95">
        <div className="flex h-[6rem] flex-col justify-start pt-1">
          <div className="flex items-center justify-between">
            <h3 className="font-sans text-xl font-semibold text-primary">
              {member.name}
            </h3>
            <div className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform duration-500 group-hover:rotate-45 group-hover:bg-primary group-hover:text-primary-foreground">
              <Plus className="size-4" />
            </div>
          </div>
          <p className="mt-1 text-sm font-medium text-muted-foreground">
            {member.position}
          </p>
        </div>

        {}
        <div className="opacity-0 transition-opacity duration-500 delay-100 group-hover:opacity-100">
          <div className="mb-4 h-px w-full bg-border/50" />
          <p className="text-sm leading-relaxed text-muted-foreground">
            {member.bio}
          </p>
        </div>
      </div>
    </div>;
}